export const STACK_STATE = {
	NONE: 'none',
	BUILDING: 'building',
	CANCELLING: 'cancelling',
	SUCCESS: 'success',
	FAILED: 'failed',
	CUSTOMIZE: 'customize',
};

export const RESOURCE_MANAGER = {
	TORQUE: 'torque',
	SLURM: 'slurm',
	NONE: 'none',
};

export const USER_AUTH_TYPE = {
	LOCAL: 'local',
	LDAP: 'ldap',
	OPENID_CONNECT: 'openid_connect',
};

export const USER_ROLE = {
	SUPER_ADMIN: 'super-admin',
	ADMIN: 'admin',
	USER: 'user',
	INSTRUCTOR: 'instructor',
	PARTICIPANT: 'participant',
	DEMO: 'demo',
};

export const isParticipant = (user) => user?.role?.includes(USER_ROLE.PARTICIPANT);

/**
 * Demo users have read-only / default-deny permissions. Used for both
 * campaign demos and portal trial users from heidiai.io. Use this to
 * disable destructive UI controls (add collaborator, destroy cluster,
 * create credentials, create stacks, etc.) with a tooltip explaining why.
 */
export const isDemo = (user) => user?.role?.includes(USER_ROLE.DEMO);

export const DEMO_DISABLED_TOOLTIP = 'Not available in demo mode';

export const IMAGE_TYPE = {
	HEIDI_STACK: 'heidi-stack',
	CUSTOM_STACK: 'custom-stack'
};

export const ACCELERATOR_TYPES = {
	NONE: '',
	NVIDIA_K80: 'nvidia-tesla-k80',
	NVIDIA_P4: 'nvidia-tesla-p4',
	NVIDIA_P100: 'nvidia-tesla-p100',
	NVIDIA_V100: 'nvidia-tesla-v100',
	NVIDIA_T4: 'nvidia-tesla-t4',
	NVIDIA_A100: 'nvidia-tesla-a100',
	TPU_V2: 'tpu-v2',
	TPU_V3: 'tpu-v3',
};

export const ACCELERATOR_LABELS = {
	[ACCELERATOR_TYPES.NONE]: 'None',
	[ACCELERATOR_TYPES.NVIDIA_K80]: 'NVIDIA Tesla K80',
	[ACCELERATOR_TYPES.NVIDIA_P4]: 'NVIDIA Tesla P4',
	[ACCELERATOR_TYPES.NVIDIA_P100]: 'NVIDIA Tesla P100',
	[ACCELERATOR_TYPES.NVIDIA_V100]: 'NVIDIA Tesla V100',
	[ACCELERATOR_TYPES.NVIDIA_T4]: 'NVIDIA Tesla T4',
	[ACCELERATOR_TYPES.NVIDIA_A100]: 'NVIDIA Tesla A100',
	[ACCELERATOR_TYPES.TPU_V2]: 'Google TPU v2',
	[ACCELERATOR_TYPES.TPU_V3]: 'Google TPU v3',
};

export const OPENID_PROVIDERS = {
	ENTRA: 'entra',
	GOOGLE: 'google',
	AWS: 'aws',
	OKTA: 'okta',
};

// Human-readable provider names for the login page and settings table.
export const OPENID_PROVIDER_LABELS = {
	[OPENID_PROVIDERS.ENTRA]: 'Microsoft Entra ID',
	[OPENID_PROVIDERS.GOOGLE]: 'Google',
	[OPENID_PROVIDERS.AWS]: 'AWS Cognito',
	[OPENID_PROVIDERS.OKTA]: 'Okta',
};

// Build mode for stacks - determines how images are built
export const BUILD_MODE = {
	MULTI_IMAGE: 'multi-image', // Legacy: separate server, node, gpu images
	UNIFIED: 'unified',         // New: single heidi image for all node types
};

// Image mode for clusters - determines which image type to use at deploy time
export const IMAGE_MODE = {
	MULTI_IMAGE: 'multi-image', // Legacy: use server/node/gpu specific images
	UNIFIED: 'unified',         // New: use unified heidi image
};

// Node states exactly as the API renders them (see the API's
// util/cluster-service/node-to-readable-node.js).
export const NODE_STATE = {
	DEPLOYING: 'Deploying',
	DESTROYING: 'Destroying',
	DOWN: 'Down',
	OFFLINE: 'Offline',
	BUSY: 'Busy',
	RUNNING: 'Running',
	AVAILABLE: 'Available',
};

// A node mid-transition can take neither action. Beyond that Deploy and Destroy
// are opposites, so each is offered only where it would actually do something:
// Deploy on a node that is not there, Destroy on one that is. Previously both
// shared a single "busy" list, which left Destroy clickable on an already Down
// node and Deploy clickable on a running one - actions that queue work and then
// do nothing.
const NODE_IN_FLIGHT = [NODE_STATE.DEPLOYING, NODE_STATE.DESTROYING];
const NODE_PRESENT = [NODE_STATE.AVAILABLE, NODE_STATE.BUSY, NODE_STATE.RUNNING, NODE_STATE.OFFLINE];

export const canDeployNode = node => {
	const state = node?.state?.state;
	return !!state && !NODE_IN_FLIGHT.includes(state) && !NODE_PRESENT.includes(state);
};

export const canDestroyNode = node => {
	const state = node?.state?.state;
	return !!state && !NODE_IN_FLIGHT.includes(state) && state !== NODE_STATE.DOWN;
};
